import { useEffect, useState } from 'react';
import type { PlayerDebugSnapshot, TransitionMetrics } from '@apm/bloom-receiver-sdk';
import type { ClientType } from '@apm/contracts';
import { percentile, type Telemetry } from '../telemetry.js';
import type { HandoffState } from '../handoff.js';
import type { ReceiverView } from '../useReceiver.js';
import type { ResumeCheckLog } from '../resume-return.js';

/**
 * `?debug=1` 패널. **MVP 검증에 필수**다 (`docs/06-frontend-spec.md`).
 *
 * 두 가지를 지킨다.
 *
 * 1. **T_freeze 를 SDK 에서 그대로 받는다.** 패널이 프레임 콜백을 따로 돌리면 그 자체가
 *    측정 교란이고, 정의가 M0 과 갈라져 M7 의 대조가 성립하지 않는다
 *    (`TransitionMetrics` 주석에 M0 정의가 그대로 있다).
 * 2. **매 프레임 갱신하지 않는다.** 계측은 `Telemetry` 버퍼에 쌓이고 여기서 낮은 주기로
 *    폴링해 그린다. 전환은 30초에 한 번이지만 패널이 그 순간에 렌더를 밀어 넣으면
 *    측정 대상 프레임을 건드린다.
 */
const POLL_MS = 500;

type Snapshot = PlayerDebugSnapshot;

export function DebugPanel({
  view,
  telemetry,
  snapshot,
  clientType,
  handoff,
  resumeCheck,
}: {
  view: ReceiverView;
  telemetry: Telemetry;
  snapshot: () => Snapshot | null;
  /** T8 — 이 값이 캐스케이드를 가르는데 **화면에는 드러나지 않는다** */
  clientType: ClientType;
  /** T8 — 마지막 핸드오프 결과. 왜 그 오버레이가 떴는지가 여기 남는다 */
  handoff: HandoffState;
  /**
   * v18 J9 — 「돌아왔을 때」의 마지막 확인.
   *
   * **이 줄이 이 확장의 유일한 관찰 창이다** (`재생 경로` 줄과 같은 성격). 이어보기가
   * 안 따라올 때 첫 질문이 **「사건이 오기는 했는가」**인데, 그 답이 화면 어디에도 없다 —
   * 아이폰에는 하네스가 붙지 않아 더욱 그렇다.
   */
  resumeCheck: ResumeCheckLog | null;
}) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTick((n) => n + 1);
    }, POLL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  // `tick` 은 폴링 신호일 뿐이다. 값 자체는 아래에서 버퍼를 직접 읽는다.
  void tick;

  const snap = snapshot();
  const natural = telemetry.transitions.filter((m) => m.trigger === 'natural');
  const freezes = natural.map((m) => m.tFreezeMs).filter((v): v is number => v !== null);
  const skips = telemetry.transitions
    .filter((m) => m.trigger === 'skip')
    .map((m) => m.tSkipMs)
    .filter((v): v is number => v !== null);
  const gapless = freezes.filter((v) => v === 0).length;
  const last = telemetry.transitions.at(-1) ?? null;

  return (
    <aside className="debug" data-i18n="ko">
      <h2 className="debug__head">디버그 (?debug=1)</h2>

      <Row label="상태" value={view.state} />
      {/*
        **판정이 틀리면 캐스케이드 전체가 어긋나는데 화면에는 그 사실이 드러나지 않는다** —
        폰에서 보는데 QR 이 뜨거나, TV 에서 보는데 Tier 0 으로 떨어져 재생이 끝난다.
        그래서 사람도 하네스도 이 값을 여기서 읽는다 (`docs/09` T8).
      */}
      <Row label="clientType" value={clientType} />
      {/*
        v12 — 어느 재생 경로가 도는가. **iPhone 에는 MSE 가 없어 hls.js 가 조용히 멈춘다**
        (`docs/30` §1). 영상이 안 나올 때 「어느 어댑터였나」가 첫 질문이고, 그 기기에는
        하네스가 붙지 않아 이 줄이 유일한 관찰 창이다.
      */}
      <Row label="재생 경로" value={view.playerKind} />
      {/*
        v36 — 어느 엔진이 채널을 잇는가(절대 규칙 93). **폴백은 조용하지 않다** — Flower 를 골랐는데 BLOOM 이 돌면
        그 이유가 다음 줄에 남는다(`bundle-load-failed` · `start-failed:<code>` · v37 부터 라이브 창은 사유가 아니다).
      */}
      <Row label="엔진" value={view.engine} />
      {/* v38 — 처음 고른 이유: `installed-app`(설치된 앱이라 Flower) · `requested` · `opted-out` · `default` · `native-hls` */}
      <Row label="엔진 이유" value={view.engineReason} />
      <Row label="엔진 폴백" value={view.engineFallback ?? '—'} />
      <Row label="handoff" value={describeHandoff(handoff)} />
      <Row label="인덱스" value={`${String(view.index + 1)} / ${String(view.total)}`} />
      <Row label="playlistId" value={view.playlistId ?? '—'} />
      <Row label="재동기화" value={describeResumeCheck(resumeCheck)} />
      <Row label="deviceId" value={view.deviceId} />

      <h3 className="debug__sub">전환 (자연 종료 {String(natural.length)}건)</h3>
      {/* 이 프로젝트에서 가장 중요한 숫자다 (docs/06). M0: p50 0ms, 무갭률 86.7% */}
      <Row label="T_freeze p50" value={ms(percentile(freezes, 50))} />
      <Row label="T_freeze p95" value={ms(percentile(freezes, 95))} />
      <Row label="T_freeze max" value={ms(freezes.length > 0 ? Math.max(...freezes) : null)} />
      <Row
        label="무갭률"
        value={
          freezes.length === 0
            ? '—'
            : `${((gapless / freezes.length) * 100).toFixed(1)}% (${String(gapless)}/${String(freezes.length)})`
        }
      />
      <Row label="T_skip p95" value={ms(percentile(skips, 95))} />
      <Row label="vsync" value={ms(snap?.vsyncMs ?? null)} />

      <h3 className="debug__sub">마지막 전환</h3>
      {last === null ? (
        <p className="debug__empty">아직 없음</p>
      ) : (
        <>
          <Row label="trigger / path" value={`${last.trigger} / ${last.path}`} />
          <Row label="T_freeze" value={ms(last.tFreezeMs)} />
          <Row label="raw" value={ms(last.rawMs)} />
          <Row label="프리롤 리드" value={ms(last.prerollLeadMs)} />
          <Row label="T_skip" value={ms(last.tSkipMs)} />
          <Row label="B 드롭 프레임" value={last.droppedB === null ? '—' : String(last.droppedB)} />
        </>
      )}

      <h3 className="debug__sub">경로 분포</h3>
      {/* `preroll` 이 아닌 값이 잦아지면 무갭이 깨지고 있다는 신호다. */}
      <Row label="" value={pathHistogram(telemetry.transitions)} />

      <h3 className="debug__sub">프리로드</h3>
      <Row label="다음 항목 적재" value={snap === null ? '—' : yesNo(snap.nextReady)} />
      <Row label="프리롤 재생 중" value={snap === null ? '—' : yesNo(snap.nextPlaying)} />
      <Row label="꼬리 무장" value={snap === null ? '—' : yesNo(snap.tailArmed)} />
      <Row label="프리롤 발화" value={snap === null ? '—' : yesNo(snap.prerolled)} />

      <h3 className="debug__sub">마지막 프레임 판정</h3>
      {/* `duration` 이 아니라 videoStart + videoSpan − 1/30 으로 판정한다 (부채 정산 3절). */}
      <Row label="videoStart" value={sec(snap?.videoStart ?? null)} />
      <Row label="videoSpan (EXTINF 합)" value={sec(snap?.videoSpan ?? null)} />
      <Row label="duration (오디오 포함)" value={sec(snap?.duration ?? null)} />
      <Row
        label="오디오 초과"
        value={
          snap === null ||
          snap.videoStart === null ||
          snap.videoSpan === null ||
          snap.duration === null
            ? '—'
            : ms((snap.duration - (snap.videoStart + snap.videoSpan)) * 1000)
        }
      />

      <h3 className="debug__sub">최근 전송 이벤트</h3>
      <ul className="debug__log">
        {telemetry.events
          .slice(-8)
          .reverse()
          .map((e, i) => (
            <li key={`${e.type}-${String(i)}-${e.productId ?? ''}`}>
              {e.type} @{String(e.positionSec ?? 0)}s
            </li>
          ))}
        {telemetry.events.length === 0 && <li className="debug__empty">아직 없음</li>}
      </ul>

      {telemetry.errors.length > 0 && (
        <>
          <h3 className="debug__sub">오류</h3>
          <ul className="debug__log">
            {telemetry.errors
              .slice(-4)
              .reverse()
              .map((e, i) => (
                <li key={`${e.code}-${String(i)}`}>{e.code}</li>
              ))}
          </ul>
        </>
      )}
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="debug__row">
      <span className="debug__label">{label}</span>
      <span className="debug__value">{value}</span>
    </div>
  );
}

function ms(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}ms`;
}

function sec(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(6)}s`;
}

function yesNo(value: boolean): string {
  return value ? '예' : '아니오';
}

function pathHistogram(all: TransitionMetrics[]): string {
  if (all.length === 0) return '—';
  const counts = new Map<string, number>();
  for (const m of all) counts.set(m.path, (counts.get(m.path) ?? 0) + 1);
  return [...counts.entries()].map(([path, n]) => `${path} ${String(n)}`).join(' · ');
}

/**
 * 핸드오프 상태 한 줄. **왜 그 오버레이가 떴는지**가 여기 남는다 — `push` 는 대수,
 * `qr` 은 대상 URL, `error` 는 이유다. 상태 이름만 찍으면 실패의 원인이 사라진다.
 */
/**
 * 「돌아왔을 때」의 마지막 확인을 한 줄로 (v18 J9).
 *
 * **`—` 와 그 밖이 가르는 것이 전부다** — `—` 면 사건이 한 번도 오지 않았다는 뜻이고,
 * 그때 원인은 이어보기 판정이 아니라 **그 사건이 오지 않는 환경**이다.
 */
function describeResumeCheck(log: ResumeCheckLog | null): string {
  if (log === null) return '—';
  const at = new Date(log.atMs).toTimeString().slice(0, 8);
  const server =
    log.serverResume === null
      ? '없음'
      : `${log.serverResume.productId.slice(-2)}@${String(log.serverResume.positionSec)}`;
  const here = `${log.currentProductId?.slice(-2) ?? '—'}@${String(Math.floor(log.elapsedSec))}`;
  return `${at} ${log.by} 서버 ${server} / 지금 ${here} → ${log.moved ? '옮김' : '그대로'}`;
}

function describeHandoff(handoff: HandoffState): string {
  switch (handoff.kind) {
    // B9. **하네스가 규칙 14 를 여기서 읽는다** — 시트가 열렸다는 사실과 그 시점의
    // `matchId` 가 화면에는 배지로만 보이고, 검증은 `window.__bloomReceiver` 로 온다.
    case 'sheet':
      // **진단은 번역하지 않는다** (D12). 사전 키를 그대로 찍는다 — 화면에 뜬 문구가
      // 어느 키에서 왔는지가 여기서 알고 싶은 것이고, 번역하면 오히려 대조가 어렵다.
      return `sheet ${handoff.matchId ?? '(기록 없음)'}${handoff.note ? ` · ${handoff.note.key}` : ''}`;
    case 'push':
      return `push ${String(handoff.count)}대`;
    case 'qr':
      return `qr ${handoff.url}`;
    case 'error':
      return `error ${handoff.reason}`;
    default:
      return handoff.kind;
  }
}
